import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Plus, Edit, Trash2, Search, Eye, X, Upload, Image as ImageIcon } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from '../../config/axios';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // create, edit, view
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    location: '',
    description: '',
    year: '',
    image: '',
    is_featured: false,
    is_published: true
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/admin/projects');
      setProjects(response.data.data.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await axios.delete(`/admin/projects/${id}`);
        alert('Project deleted successfully!');
        fetchProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Failed to delete project');
      }
    }
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({
      title: '',
      category: '',
      location: '',
      description: '',
      year: '',
      image: '',
      is_featured: false,
      is_published: true
    });
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEditModal = (project) => {
    setModalMode('edit');
    setSelectedProject(project);
    setFormData({
      title: project.title,
      category: project.category,
      location: project.location,
      description: project.description,
      year: project.year,
      image: project.image || '',
      is_featured: project.is_featured,
      is_published: project.is_published
    });
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openViewModal = (project) => {
    setModalMode('view');
    setSelectedProject(project);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProject(null);
    setImageFile(null);
    setImagePreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let imageUrl = formData.image;
      
      // Upload image if file is selected
      if (imageFile) {
        try {
          const imageFormData = new FormData();
          imageFormData.append('image', imageFile);
          
          console.log('Uploading image:', imageFile.name, imageFile.type, imageFile.size);
          
          // Override default JSON content-type - let browser set multipart/form-data with boundary
          const uploadResponse = await axios.post('/admin/upload-image', imageFormData, {
            headers: {
              'Content-Type': undefined // This allows browser to set correct multipart boundary
            }
          });
          
          console.log('Upload response:', uploadResponse.data);
          
          if (uploadResponse.data.success) {
            imageUrl = uploadResponse.data.url;
          } else {
            throw new Error(uploadResponse.data.message || 'Image upload failed');
          }
        } catch (uploadError) {
          console.error('Image upload error:', uploadError);
          console.error('Error details:', uploadError.response?.data);
          console.error('Validation errors:', uploadError.response?.data?.errors);
          
          // Log each validation error in detail
          if (uploadError.response?.data?.errors?.image) {
            console.error('Image validation errors:', uploadError.response.data.errors.image);
          }
          
          // Show specific validation errors if available
          if (uploadError.response?.data?.errors) {
            const errorMessages = Object.values(uploadError.response.data.errors).flat();
            alert('Upload failed:\n' + errorMessages.join('\n'));
          }
          
          throw new Error(uploadError.response?.data?.message || 'Failed to upload image');
        }
      }
      
      const projectData = {
        ...formData,
        image: imageUrl
      };

      if (modalMode === 'create') {
        await axios.post('/admin/projects', projectData);
        alert('Project created successfully!');
      } else if (modalMode === 'edit') {
        await axios.put(`/admin/projects/${selectedProject.id}`, projectData);
        alert('Project updated successfully!');
      }
      closeModal();
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      alert(error.response?.data?.message || 'Failed to save project');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredProjects = projects.filter(project =>
    project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <Helmet>
        <title>Projects Management - Admin Portal | BURALE</title>
      </Helmet>

      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-burale-dark mb-2">Projects Management</h1>
            <p className="text-gray-600">Manage all projects and portfolio items</p>
          </div>
          <button 
            onClick={openCreateModal}
            className="bg-burale-teal text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>Add New Project</span>
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search projects by title, category, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-burale-teal"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Year</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredProjects.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No projects found
                    </td>
                  </tr>
                ) : (
                  filteredProjects.map((project) => (
                    <tr key={project.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-burale-dark">{project.title}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-burale-teal/10 text-burale-teal rounded-full text-sm font-medium">
                          {project.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600">{project.location}</td>
                      <td className="px-6 py-4 text-gray-600">{project.year}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col space-y-1">
                          {project.is_published && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">Published</span>
                          )}
                          {project.is_featured && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">Featured</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => openViewModal(project)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" 
                            title="View"
                          >
                            <Eye size={18} />
                          </button>
                          <button 
                            onClick={() => openEditModal(project)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors" 
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button 
                            onClick={() => handleDelete(project.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" 
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-burale-dark">
                {modalMode === 'create' && 'Add New Project'}
                {modalMode === 'edit' && 'Edit Project'}
                {modalMode === 'view' && 'Project Details'}
              </h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              {modalMode === 'view' ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Title</label>
                    <p className="text-lg text-burale-dark">{selectedProject?.title}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Category</label>
                      <p className="text-burale-dark">{selectedProject?.category}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Location</label>
                      <p className="text-burale-dark">{selectedProject?.location}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Year</label>
                    <p className="text-burale-dark">{selectedProject?.year}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Description</label>
                    <p className="text-burale-dark whitespace-pre-wrap">{selectedProject?.description}</p>
                  </div>
                  <div className="flex gap-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Featured</label>
                      <p className="text-burale-dark">{selectedProject?.is_featured ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Published</label>
                      <p className="text-burale-dark">{selectedProject?.is_published ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-burale-dark mb-2">Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-burale-dark mb-2">Category *</label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none"
                      >
                        <option value="">Select Category</option>
                        <option value="Building">Building</option>
                        <option value="Civil Works">Civil Works</option>
                        <option value="Roads">Roads</option>
                        <option value="Water">Water</option>
                        <option value="Infrastructure">Infrastructure</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-burale-dark mb-2">Year *</label>
                      <input
                        type="text"
                        name="year"
                        value={formData.year}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-burale-dark mb-2">Location *</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-burale-dark mb-2">Description *</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      rows="4"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none"
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-burale-dark mb-2">
                      Project Image
                      <span className="text-xs font-normal text-gray-500 ml-2">(Recommended: 1200x800px or 3:2 ratio)</span>
                    </label>
                    <div className="space-y-3">
                      {/* Image Preview */}
                      {(imagePreview || formData.image) && (
                        <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden">
                          <img 
                            src={imagePreview || formData.image} 
                            alt="Preview" 
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setImageFile(null);
                              setImagePreview(null);
                              setFormData({...formData, image: ''});
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      )}
                      
                      {/* File Upload */}
                      <div className="flex items-center justify-center w-full">
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-10 h-10 mb-3 text-gray-400" />
                            <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                          </div>
                          <input 
                            type="file" 
                            className="hidden" 
                            accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                // Check file size (10MB max)
                                const maxSize = 10 * 1024 * 1024; // 10MB in bytes
                                if (file.size > maxSize) {
                                  alert('File size must be less than 10MB');
                                  e.target.value = '';
                                  return;
                                }
                                
                                // Check file type
                                const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
                                if (!allowedTypes.includes(file.type)) {
                                  alert('Only JPEG, PNG, GIF, and WebP images are allowed');
                                  e.target.value = '';
                                  return;
                                }
                                
                                setImageFile(file);
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setImagePreview(reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </label>
                      </div>
                      
                      {/* Or Image URL */}
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-300"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-white text-gray-500">Or enter image URL</span>
                        </div>
                      </div>
                      <input
                        type="text"
                        name="image"
                        value={formData.image}
                        onChange={handleInputChange}
                        placeholder="https://example.com/image.jpg"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex gap-6">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="is_featured"
                        checked={formData.is_featured}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-burale-teal focus:ring-burale-teal border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">Featured Project</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="is_published"
                        checked={formData.is_published}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-burale-teal focus:ring-burale-teal border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">Published</span>
                    </label>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-6 py-2 bg-burale-teal text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50"
                    >
                      {submitting ? 'Saving...' : (modalMode === 'create' ? 'Create Project' : 'Update Project')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Projects;
